
import { useState, useEffect, useCallback } from 'react';
import { fetchLocationHistory, LocationPoint } from '@/components/bikes/utils/locationHistoryUtils';
import { toast } from '@/components/ui/sonner';

export const useBikeLocationHistory = (
  bikeId?: string,
  courierId?: string,
  dateFrom?: Date,
  dateTo?: Date,
  refreshTrigger?: number,
  filterByMoscowRegion?: boolean
) => {
  const [locationHistory, setLocationHistory] = useState<LocationPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [averageSpeed, setAverageSpeed] = useState<number>(0);

  const fetchData = useCallback(async (forceRefresh: boolean = false) => {
    if (!dateFrom || !dateTo) {
      console.log('Даты не определены, пропускаем загрузку');
      return;
    }

    if (!bikeId && !courierId) {
      console.log('ID велосипеда или курьера не определены, пропускаем загрузку');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Загружаем историю местоположений:', {
        bikeId,
        courierId,
        dateFrom,
        dateTo,
        forceRefresh,
        filterByMoscowRegion
      });

      const history = await fetchLocationHistory(
        dateFrom,
        dateTo,
        bikeId,
        courierId,
        forceRefresh,
        filterByMoscowRegion || false
      );

      setLocationHistory(history);

      // Рассчитываем среднюю скорость
      if (history.length > 0) {
        const speeds = history
          .filter(point => point.speed !== undefined && point.speed > 0)
          .map(point => point.speed!);
        
        const avgSpeed = speeds.length > 0 
          ? speeds.reduce((sum, speed) => sum + speed, 0) / speeds.length
          : 0;
        
        setAverageSpeed(avgSpeed);
      } else {
        setAverageSpeed(0);
      }

      console.log(`Загружено ${history.length} точек истории местоположений`);
    } catch (err) {
      console.error('Ошибка при загрузке истории местоположений:', err);
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      setError(errorMessage);
      toast.error(`Ошибка загрузки истории: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [bikeId, courierId, dateFrom, dateTo, filterByMoscowRegion]);

  // Функция для принудительного обновления
  const refetch = useCallback(async () => {
    console.log('Принудительное обновление истории местоположений');
    await fetchData(true);
  }, [fetchData]);

  // Основной эффект для загрузки данных
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Эффект для обработки принудительного обновления
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      console.log('Получен триггер обновления:', refreshTrigger);
      refetch();
    }
  }, [refreshTrigger, refetch]);

  return {
    locationHistory,
    loading,
    error,
    averageSpeed,
    refetch,
    refresh: refetch, // добавляем алиас для совместимости
    data: locationHistory // добавляем алиас data для совместимости
  };
};
